import type { FC } from 'react';
import type { ChartElements } from './chartElements';
import { GC_INNER_H, GC_PAD_LEFT, GC_PAD_TOP } from './layout';

/**
 * The data, clipped to the plot. Rects and lines are drawn in normalised coordinates under
 * one transform; dots and texts are converted to pixels, so the non-uniform scale does not
 * distort them.
 */
export const PlotLayers: FC<{ elements: ChartElements; innerWidth: number }> = ({
  elements: { rects, lines, dots, texts },
  innerWidth,
}) => {
  const dataTransform = `translate(${GC_PAD_LEFT}, ${GC_PAD_TOP + GC_INNER_H}) scale(${innerWidth}, ${-GC_INNER_H})`;
  return (
    <>
      <g clipPath="url(#gc-plot-area)">
        <g transform={dataTransform}>
          {rects.map((r, i) => (
            <rect
              key={`${r.x}-${r.y}-${i}`}
              x={r.x}
              y={r.y}
              width={r.width}
              height={r.height}
              fill={r.fillColor}
            />
          ))}
          {lines.map((l, i) => (
            <line
              key={`${l.x1}-${l.x2}-${i}`}
              x1={l.x1}
              y1={l.y1}
              x2={l.x2}
              y2={l.y2}
              stroke={l.color}
              strokeWidth={l.width ?? 1}
              vectorEffect="non-scaling-stroke"
              strokeDasharray={l.striped ? '4 2' : undefined}
            />
          ))}
        </g>
      </g>

      <g clipPath="url(#gc-plot-area)">
        {dots.toReversed().map((d, i) => (
          <circle
            key={`${d.type}-${d.time}-${i}`}
            cx={GC_PAD_LEFT + d.time * innerWidth}
            cy={GC_PAD_TOP + (1 - d.offset) * GC_INNER_H}
            r={d.isBig ? 3.2 : 2.6}
            fill={d.color}
            stroke={d.strokeColor}
            strokeWidth={d.strokeColor != null ? 1.0 : 0}
          />
        ))}
      </g>

      <g clipPath="url(#gc-plot-area)">
        {texts.map((t, i) => {
          const px = GC_PAD_LEFT + t.x * innerWidth - (t.orientation === 'vertical' ? 2 : 0);
          const py = GC_PAD_TOP + (1 - t.y) * GC_INNER_H - (t.orientation === 'vertical' ? 0 : 2);
          return (
            <text
              key={`${t.x}-${t.y}-${i}`}
              x={px}
              y={py}
              fill={t.color}
              fontSize={8}
              dominantBaseline="auto"
              textAnchor="middle"
              transform={
                t.angle != null
                  ? `rotate(${t.angle}, ${px}, ${py})`
                  : t.orientation === 'vertical'
                    ? `rotate(-90, ${px}, ${py})`
                    : undefined
              }
            >
              {t.content}
            </text>
          );
        })}
      </g>
    </>
  );
};
